import { PER_USER_PROJECT_FIELDS } from '@harness/shared';
import type { QueryClient } from '@tanstack/react-query';
import type { ConversationDTO, GateDTO, MessageDTO, ProjectDTO, StreamEvent } from '@harness/shared';
import { qk } from './keys';
import { toast } from './store';

export interface ConversationData {
  conversation: ConversationDTO;
  messages: MessageDTO[];
  hasMore: boolean;
  oldestSeq: number | null;
  gates: GateDTO[];
  nextModelId: string | null;
  activeAgentId: string | null;
}

function patchConversation(qc: QueryClient, id: string, fn: (d: ConversationData) => ConversationData) {
  qc.setQueryData<ConversationData>(qk.conversation(id), (d) => (d ? fn(d) : d));
}

function upsertMessage(list: MessageDTO[], m: MessageDTO) {
  const i = list.findIndex((x) => x.id === m.id);
  if (i < 0) return [...list, m];
  const next = list.slice();
  next[i] = m;
  return next;
}

/**
 * One EventSource per tab. Agent turns run on the server as background jobs; this stream
 * patches the react-query cache so every view updates live (and keeps working across
 * project switches and refreshes).
 */
export function connectEvents(qc: QueryClient) {
  let es: EventSource | null = null;
  let closed = false;
  let retry = 1000;
  let hadError = false;

  const handle = (e: StreamEvent) => {
    switch (e.type) {
      case 'message.created':
      case 'message.updated':
        patchConversation(qc, e.message.conversationId, (d) => ({ ...d, messages: upsertMessage(d.messages, e.message) }));
        break;
      case 'message.deleted':
        patchConversation(qc, e.conversationId, (d) => ({ ...d, messages: d.messages.filter((m) => m.id !== e.messageId) }));
        break;
      case 'message.delta':
        patchConversation(qc, e.conversationId, (d) => ({
          ...d,
          messages: d.messages.map((m) => {
            if (m.id !== e.messageId) return m;
            const parts = m.parts.slice();
            const cur = parts[e.partIndex];
            if (cur && cur.type === e.kind) parts[e.partIndex] = { ...cur, text: cur.text + e.text };
            else parts[e.partIndex] = { type: e.kind, text: e.text };
            return { ...m, parts, streaming: true };
          }),
        }));
        break;
      case 'toolcall.updated':
        patchConversation(qc, e.conversationId, (d) => ({
          ...d,
          messages: d.messages.map((m) =>
            m.id === e.toolCall.messageId
              ? { ...m, toolCalls: m.toolCalls.some((t) => t.id === e.toolCall.id) ? m.toolCalls.map((t) => (t.id === e.toolCall.id ? e.toolCall : t)) : [...m.toolCalls, e.toolCall] }
              : m,
          ),
        }));
        break;
      case 'conversation.updated': {
        const c = e.conversation;
        patchConversation(qc, c.id, (d) => ({ ...d, conversation: c }));
        qc.setQueryData<ConversationDTO[]>(qk.conversations(e.projectId), (list) => {
          if (!list) return list;
          const without = list.filter((x) => x.id !== c.id);
          const next = [c, ...without];
          return next.sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.createdAt.localeCompare(a.createdAt));
        });
        break;
      }
      case 'gate.updated':
        patchConversation(qc, e.conversationId, (d) => ({
          ...d,
          gates: d.gates.some((g) => g.id === e.gate.id) ? d.gates.map((g) => (g.id === e.gate.id ? e.gate : g)) : [...d.gates, e.gate],
        }));
        void qc.invalidateQueries({ queryKey: qk.conversations(e.projectId) });
        break;
      case 'feature.updated':
        qc.setQueryData(qk.feature(e.run.id), e.run);
        void qc.invalidateQueries({ queryKey: qk.features(e.projectId) });
        break;
      case 'project.updated':
        // Project-wide events don't carry per-person fields (your pin, last conversation, role): keep yours.
        qc.setQueryData<ProjectDTO[]>(qk.projects, (list) => list?.map((p) => (p.id === e.project.id ? { ...e.project, ...keepMine(p) } : p)));
        qc.setQueryData(qk.project(e.project.id), (old: ProjectDTO | undefined) => (old ? { ...old, ...e.project, ...keepMine(old) } : old));
        break;
      case 'environment.updated':
        void qc.invalidateQueries({ queryKey: qk.environment(e.projectId) });
        void qc.invalidateQueries({ queryKey: qk.server(e.projectId) });
        break;
      case 'wireframe.updated':
        void qc.invalidateQueries({ queryKey: ['wireframes', e.conversationId] });
        void qc.invalidateQueries({ queryKey: qk.conversations(e.projectId) });
        break;
      case 'ticketspec.updated':
        qc.setQueryData(['ticket-spec', e.conversationId], e.spec);
        void qc.invalidateQueries({ queryKey: qk.conversations(e.projectId) });
        break;
      case 'memory.updated':
        void qc.invalidateQueries({ queryKey: qk.memory(e.projectId) });
        break;
      case 'agent.updated':
        void qc.invalidateQueries({ queryKey: qk.agents(e.projectId) });
        void qc.invalidateQueries({ queryKey: qk.agentVersions(e.agent.id) });
        break;
      case 'jira.updated':
        void qc.invalidateQueries({ queryKey: qk.issues(e.projectId) });
        break;
      case 'repos.updated':
        void qc.invalidateQueries({ queryKey: qk.integrations(e.projectId) });
        break;
      case 'toast':
        toast(e.text);
        break;
    }
  };

  // Re-fetch what's on screen (anything streamed while we weren't listening).
  const resync = () => {
    void qc.invalidateQueries({ queryKey: ['conversation'] });
    void qc.invalidateQueries({ queryKey: ['conversations'] });
    void qc.invalidateQueries({ queryKey: ['feature'] });
    void qc.invalidateQueries({ queryKey: qk.projects });
  };

  // Watchdog: the server sends a ping every 20 s. A proxy can leave a stream open after the
  // server restarts, with nothing ever arriving, so silence means reconnect.
  let lastSeen = Date.now();
  const watchdog = setInterval(() => {
    if (!closed && Date.now() - lastSeen > 30_000) {
      lastSeen = Date.now();
      es?.close();
      hadError = true;
      open();
    }
  }, 5_000);
  const onVisible = () => {
    if (document.visibilityState === 'visible') resync();
  };
  document.addEventListener('visibilitychange', onVisible);

  const open = () => {
    if (closed) return;
    es = new EventSource('/api/events');
    es.onmessage = (m) => {
      lastSeen = Date.now();
      try {
        const e = JSON.parse(m.data) as StreamEvent;
        if (e.type !== 'ping' && e.type !== 'hello') handle(e);
      } catch (err) {
        console.error('bad event', err);
      }
    };
    es.onopen = () => {
      retry = 1000;
      // After a drop, refetch what's on screen so nothing streamed while offline is missed.
      lastSeen = Date.now();
      if (hadError) {
        hadError = false;
        resync();
      }
    };
    es.onerror = () => {
      hadError = true;
      es?.close();
      if (!closed) setTimeout(open, (retry = Math.min(retry * 2, 15_000)));
    };
  };
  open();
  return () => {
    closed = true;
    clearInterval(watchdog);
    document.removeEventListener('visibilitychange', onVisible);
    es?.close();
  };
}

function keepMine(p: ProjectDTO) {
  return Object.fromEntries(PER_USER_PROJECT_FIELDS.map((k) => [k, p[k]])) as Pick<ProjectDTO, (typeof PER_USER_PROJECT_FIELDS)[number]>;
}
